/**
 * Lecture defensive des charges utiles du journal de synchronisation.
 *
 * Le serveur **ne valide pas** `payload_json` : il le range tel que l'appareil l'a
 * pousse (ADR 0035 §1). Sa forme varie selon l'enregistrement (`{session}`,
 * `{measurement}`, `{record}`, `{batch}`, `{batch, movement}`,
 * `{movement, affectedBatches}`, `{revision}`…), un build ancien peut ne pas
 * porter un champ recent, et rien n'interdit un `null`, une chaine ou un tableau
 * a la racine. Tout ce qui lit une charge passe donc par ce module, dont aucune
 * fonction ne **leve** : un champ absent ou d'un autre type rend `null`, une forme
 * qu'on ne sait pas parcourir se rend en JSON brut (`rawJson`).
 *
 * Module **pur** : aucune dependance a React, testable sur des charges copiees du
 * mobile.
 */

export const MISSING = '—';

/** Ce qu'un champ de texte libre retire par le serveur (role `support`) dit a la place de sa valeur. */
export const MASKED = 'Masqué pour votre rôle';

export type Scalar = string | number | boolean | null;

export type PayloadRecord = Readonly<Record<string, unknown>>;

export const isRecord = (value: unknown): value is PayloadRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const stringOf = (record: PayloadRecord | null, key: string): string | null => {
  const value = record?.[key];

  return typeof value === 'string' && value.trim() !== '' ? value : null;
};

export const numberOf = (record: PayloadRecord | null, key: string): number | null => {
  const value = record?.[key];

  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

export const listOf = (record: PayloadRecord | null, key: string): readonly unknown[] | null => {
  const value = record?.[key];

  return Array.isArray(value) ? (value as readonly unknown[]) : null;
};

/** Enveloppe lue : sa cle (`session`) et son contenu. */
export interface Section {
  readonly key: string;
  readonly value: PayloadRecord;
}

/** Premiere enveloppe de `keys` presente et de forme objet, ou `null`. */
export const sectionOf = (payload: unknown, keys: readonly string[]): Section | null => {
  if (!isRecord(payload)) {
    return null;
  }

  for (const key of keys) {
    const value = payload[key];

    if (isRecord(value)) {
      return { key, value };
    }
  }

  return null;
};

/** JSON indente d'une valeur quelconque ; une valeur non serialisable rend sa description. */
export const rawJson = (value: unknown): string => {
  try {
    // `JSON.stringify(undefined)` rend `undefined`, que le type ne dit pas.
    const text = JSON.stringify(value, null, 2) as string | undefined;

    return text ?? String(value);
  } catch {
    return String(value);
  }
};

// ---------------------------------------------------------------------------
// Mise en forme des valeurs
// ---------------------------------------------------------------------------

const NUMBER = new Intl.NumberFormat('fr-FR');
const DATE_TIME = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'medium' });
const DATE_TIME_SHORT = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
const DAY = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeZone: 'UTC' });

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Instant lisible, a la minute (tableau) ou a la seconde (detail). `—` si illisible. */
export const formatInstant = (iso: string | null, precise = false): string => {
  if (iso === null || iso === '') {
    return MISSING;
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return MISSING;
  }

  return (precise ? DATE_TIME : DATE_TIME_SHORT).format(date);
};

const COMPACT = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const COMPACT_WITH_YEAR = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * Instant sur une ligne de tableau : sans l'annee quand c'est l'annee en cours
 * (« 4 oct. 09:12 »), avec sinon. Le detail, lui, ecrit tout, secondes comprises.
 */
export const formatCompactInstant = (iso: string | null, now: Date = new Date()): string => {
  if (iso === null || iso === '') {
    return MISSING;
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return MISSING;
  }

  return (date.getFullYear() === now.getFullYear() ? COMPACT : COMPACT_WITH_YEAR).format(date);
};

/** `45 s`, `15 min`, `1 h 05`. */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${NUMBER.format(seconds)} s`;
  }

  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);

  if (minutes < 60) {
    return rest === 0 ? `${String(minutes)} min` : `${String(minutes)} min ${String(rest)} s`;
  }

  return `${String(Math.floor(minutes / 60))} h ${String(minutes % 60).padStart(2, '0')}`;
};

/** Unite deduite du **nom** du champ (`quantityMl`, `durationSeconds`…). */
const UNIT_SUFFIXES: readonly (readonly [string, string])[] = [
  ['Ml', 'ml'],
  ['Grams', 'g'],
  ['Millimeters', 'mm'],
  ['Celsius', '°C'],
];

const lastSegment = (path: string): string => {
  const parts = path.split('.').filter((part) => !/^\d+$/.test(part));

  return parts[parts.length - 1] ?? path;
};

/**
 * Valeur d'une feuille, telle qu'on la lit : `null` en tiret, booleen en
 * Oui/Non, instants et jours en francais, unite lue du nom du champ.
 */
export const formatScalar = (path: string, value: Scalar): string => {
  if (value === null) {
    return MISSING;
  }

  if (typeof value === 'boolean') {
    return value ? 'Oui' : 'Non';
  }

  const name = lastSegment(path);

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return MISSING;
    }

    if (name.endsWith('Seconds')) {
      return formatDuration(value);
    }

    const unit = UNIT_SUFFIXES.find(([suffix]) => name.endsWith(suffix))?.[1];

    return unit === undefined ? NUMBER.format(value) : `${NUMBER.format(value)} ${unit}`;
  }

  if (value.trim() === '') {
    return MISSING;
  }

  if (ISO_INSTANT.test(value)) {
    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? value : DATE_TIME.format(date);
  }

  if (ISO_DAY.test(value)) {
    const date = new Date(`${value}T00:00:00Z`);

    return Number.isNaN(date.getTime()) ? value : DAY.format(date);
  }

  return value;
};

// ---------------------------------------------------------------------------
// Aplatissement et differences
// ---------------------------------------------------------------------------

export interface Leaf {
  readonly path: string;
  readonly value: Scalar;
}

const MAX_DEPTH = 8;
const MAX_LEAVES = 400;

const isScalar = (value: unknown): value is Scalar =>
  value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

/**
 * Feuilles d'une valeur, chemin pointe (`session.leftDurationSeconds`,
 * `affectedBatches.0.status`). Une liste de scalaires est une seule feuille
 * (« a, b ») ; une liste vide est « Aucun ». Au-dela de `MAX_DEPTH` niveaux ou
 * `MAX_LEAVES` feuilles, le reste est **dit** (une feuille de resume) et non
 * tronque en silence.
 */
export const flatten = (value: unknown, prefix = ''): readonly Leaf[] => {
  const leaves: Leaf[] = [];
  let dropped = 0;

  const push = (path: string, leaf: Scalar): void => {
    if (leaves.length >= MAX_LEAVES) {
      dropped += 1;

      return;
    }

    leaves.push({ path, value: leaf });
  };

  const walk = (node: unknown, path: string, depth: number): void => {
    if (isScalar(node)) {
      push(path, node);

      return;
    }

    if (depth >= MAX_DEPTH) {
      push(path, rawJson(node).replace(/\s+/g, ' '));

      return;
    }

    if (Array.isArray(node)) {
      const items = node as readonly unknown[];

      if (items.length === 0) {
        push(path, 'Aucun');
      } else if (items.every(isScalar)) {
        push(path, items.map((item) => (item === null ? MISSING : String(item))).join(', '));
      } else {
        items.forEach((item, index) => {
          walk(item, path === '' ? String(index) : `${path}.${String(index)}`, depth + 1);
        });
      }

      return;
    }

    if (isRecord(node)) {
      for (const [key, child] of Object.entries(node)) {
        walk(child, path === '' ? key : `${path}.${key}`, depth + 1);
      }

      return;
    }

    // `undefined`, fonction, symbole : impossibles depuis du JSON, tolerees.
    push(path, rawJson(node));
  };

  walk(value, prefix, 0);

  if (dropped > 0) {
    leaves.push({ path: '…', value: `${String(dropped)} champs de plus non affichés` });
  }

  return leaves;
};

export interface FieldChange {
  readonly path: string;
  readonly before: string;
  readonly after: string;
  /**
   * Champ de texte libre retire par le serveur (role `support`), de quel cote :
   * `before` ou `after` (l'autre cote est visible, `null` ou absent), ou `both` —
   * alors rien ne se compare, et `before` et `after` valent `MASKED`.
   */
  readonly masked?: 'before' | 'after' | 'both';
}

/**
 * Champs d'appareil qui changent a **chaque** revision sans rien dire du contenu :
 * les montrer noierait la correction (« 120 → 150 ml ») sous quatre lignes de
 * comptabilite.
 */
const BOOKKEEPING = new Set(['updatedAt', 'version', 'syncStatus', 'lastSyncedAt']);

/**
 * Differences entre deux instantanes **complets** consecutifs.
 *
 * `format` donne a une valeur son libelle (les enums du mobile se lisent en
 * francais : `breast_milk`, pas la forme machine).
 *
 * **Les champs masques ne se comparent pas.** Le serveur retire la valeur d'un
 * texte libre non nul et laisse un `null` visible : lire les deux instantanes tels
 * quels ecrirait « — → — » pour une correction que le role ne voit pas, ou
 * « aucun champ modifie » pour une version qui n'a change que la note. Un chemin
 * de `redactedBefore` / `redactedAfter` est donc rendu a part, marque `masked`.
 *
 * `null` quand ils ne sont pas comparables : l'un n'est pas un objet, ou les
 * enveloppes different (`{batch}` puis `{revision}` ne portent pas les memes
 * champs : comparer rendrait toute la fiche « ajoutee »). L'appelant affiche
 * alors la version telle quelle plutot qu'une difference fausse.
 */
export const diffSnapshots = (
  previous: unknown,
  next: unknown,
  format: (path: string, value: Scalar) => string = formatScalar,
  redactedBefore: readonly string[] = [],
  redactedAfter: readonly string[] = [],
): readonly FieldChange[] | null => {
  if (!isRecord(previous) || !isRecord(next)) {
    return null;
  }

  const shape = (record: PayloadRecord): string => Object.keys(record).sort().join('|');

  if (shape(previous) !== shape(next)) {
    return null;
  }

  const index = (value: unknown): Map<string, Scalar> =>
    new Map(flatten(value).filter((leaf) => !BOOKKEEPING.has(lastSegment(leaf.path))).map((leaf) => [leaf.path, leaf.value]));

  const before = index(previous);
  const after = index(next);
  const changes: FieldChange[] = [];
  const maskedBefore = new Set(redactedBefore);
  const maskedAfter = new Set(redactedAfter);
  const masked = (path: string): boolean => maskedBefore.has(path) || maskedAfter.has(path);

  for (const [path, value] of after) {
    if (masked(path)) {
      continue;
    }

    const old = before.has(path) ? before.get(path)! : null;

    if (!before.has(path) || old !== value) {
      changes.push({ path, before: format(path, old), after: format(path, value) });
    }
  }

  for (const [path, value] of before) {
    if (!after.has(path) && !masked(path)) {
      changes.push({ path, before: format(path, value), after: MISSING });
    }
  }

  for (const path of new Set([...maskedBefore, ...maskedAfter])) {
    if (BOOKKEEPING.has(lastSegment(path))) {
      continue;
    }

    const side = maskedBefore.has(path) ? (maskedAfter.has(path) ? 'both' : 'before') : 'after';
    // L'autre cote est visible : `null` (le serveur ne retire que du non nul) ou absent.
    const visible = (map: Map<string, Scalar>): string => format(path, map.get(path) ?? null);

    changes.push({
      path,
      before: side === 'before' || side === 'both' ? MASKED : visible(before),
      after: side === 'after' || side === 'both' ? MASKED : visible(after),
      masked: side,
    });
  }

  return changes;
};
